import React, { createContext, useState, useContext, useEffect } from "react";
import {
  login as loginService,
  logout as logoutService,
} from "../services/authService";
import apiClient, { setAccessToken } from "../services/apiClient";
import { User } from "../models/User";

// interface User {
//   adminID: number;
//   email: string;
//   firstName: string;
//   lastName: string;
// }

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoggedIn: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem("user");
      const refreshToken = localStorage.getItem("refreshToken");

      if (storedUser && refreshToken) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        try {
          const response = await apiClient.post("/auth/refresh-token", {
            refreshToken,
          });
          setAccessToken(response.data.accessToken);
          // Update user if the server returns updated user info
          if (response.data.user) {
            setUser(response.data.user);
            localStorage.setItem("user", JSON.stringify(response.data.user));
          }
        } catch (error) {
          console.error("Token refresh failed", error);
          // Clear stored data if refresh fails
          setUser(null);
          localStorage.removeItem("user");
          localStorage.removeItem("refreshToken");
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { user, accessToken, refreshToken } = await loginService(
        email,
        password
      );
      setUser(user);
      setAccessToken(accessToken); //persist in memory
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("refreshToken", refreshToken);
    } catch (error) {
      console.error("Login failed", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      await apiClient.post("/logout", { refreshToken });
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      setUser(null);
      setAccessToken(null);
      localStorage.removeItem("user");
      localStorage.removeItem("refreshToken");
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, login, logout, isLoggedIn: !!user, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
